import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import FaceIdentityBrandLogo from '../../face-identity/components/FaceIdentityBrandLogo';
import FaceIdentityFlowNav from '../../face-identity/components/FaceIdentityFlowNav';
import { useFaceIdentityOrgLogo } from '../../face-identity/hooks/useFaceIdentityOrgLogo';
import { useFaceIdentityScrollShell } from '../../face-identity/hooks/useFaceIdentityScrollShell';
import { useKioskLandingConfig } from '../../face-identity/hooks/useKioskLandingConfig';
import { buildKioskNavState } from '../../face-identity/utils/kioskNavState';
import {
  KIOSK_PAGE_KEYS,
  showMobileLookupOption,
} from '../../face-identity/utils/kioskLandingDefaults';
import { SALON_GUEST_INTENT_PATH, SALON_HUB_PATH, SALON_STYLIST_CHECKIN_PATH } from '../config/salonApp';
import { KIOSK_MODE_GUEST, KIOSK_MODE_DESK, isSalonDeskMode, setSalonKioskMode } from '../utils/salonKioskMode';
import { captureDeviceInfo } from '../utils/deviceCapture';
import { clearSalonFaceSession } from '../utils/salonFaceSession';
import { resetWalkInGuestCheckinState } from '../utils/guestSalonCheckin';
import { resolveSalonHomePath } from '../utils/salonHomeNav';
import '../styles/EnterpriseSalonLuxTheme.css';
import '../../face-identity/styles/FaceIdentityLuxTheme.css';
import '../../face-identity/styles/FaceIdentityScrollShell.css';
import './SalonStartChoicePage.css';

export default function SalonStartChoicePage() {
  useFaceIdentityScrollShell();
  const navigate = useNavigate();
  const location = useLocation();

  const { orgLogoUrl, showLogo, onLogoError } = useFaceIdentityOrgLogo();
  const { config } = useKioskLandingConfig(KIOSK_PAGE_KEYS.SALON_ENTRY);
  const showMobile = showMobileLookupOption(config);

  const navState = buildKioskNavState(location.state, {
    mode: 'salon',
    next: location.state?.next || SALON_HUB_PATH,
    deviceInfo: location.state?.deviceInfo || captureDeviceInfo(),
  });

  useEffect(() => {
    if (isSalonDeskMode()) {
      navigate(SALON_STYLIST_CHECKIN_PATH, { replace: true });
    }
  }, [navigate]);

  const goStylistCheckIn = () => {
    setSalonKioskMode(KIOSK_MODE_DESK);
    navigate(SALON_STYLIST_CHECKIN_PATH, { replace: false });
  };

  const goGuestCheckIn = () => {
    resetWalkInGuestCheckinState();
    setSalonKioskMode(KIOSK_MODE_GUEST);
    navigate(SALON_GUEST_INTENT_PATH, {
      replace: false,
      state: { deviceInfo: navState.deviceInfo, mode: 'salon' },
    });
  };

  const goFaceIdentity = () => {
    clearSalonFaceSession();
    navigate('/nyafaz-ep/salon/scan', {
      replace: false,
      state: {
        ...navState,
        next: SALON_GUEST_INTENT_PATH,
      },
    });
  };

  const goMobileLookup = () => {
    clearSalonFaceSession();
    navigate('/nyafaz-ep/salon/mobile', {
      replace: false,
      state: navState,
    });
  };

  const goCustomerCode = () => {
    clearSalonFaceSession();
    navigate('/nyafaz-ep/salon/customer-code', {
      replace: false,
      state: navState,
    });
  };

  return (
    <div className="salon-start-choice page-bg fi-lux-theme fi-scroll-page">
      <div className="salon-start-choice__stage">
        <header className="salon-start-choice__brand">
          <FaceIdentityBrandLogo
            showLogo={showLogo}
            orgLogoUrl={orgLogoUrl}
            onLogoError={onLogoError}
            imgClassName="salon-start-choice__logo"
            fallbackClassName="salon-start-choice__logo-text"
          />
          <FaceIdentityFlowNav
            onBack={() => navigate('/nyafaz-ep/salon', { replace: true })}
            homePath={resolveSalonHomePath()}
          />
        </header>

        <main className="salon-start-choice__main">
          <p className="salon-start-choice__eyebrow">Salon</p>
          <h1 className="salon-start-choice__title">Continue</h1>
          <div className="salon-start-choice__actions">
            <button
              type="button"
              className="salon-start-choice__btn salon-start-choice__btn--primary"
              onClick={goStylistCheckIn}
            >
              Stylist check-in (mobile)
            </button>
            <button
              type="button"
              className="salon-start-choice__btn salon-start-choice__btn--ghost"
              onClick={goGuestCheckIn}
            >
              Guest kiosk (legacy)
            </button>
            <button
              type="button"
              className="salon-start-choice__btn salon-start-choice__btn--ghost"
              onClick={goCustomerCode}
            >
              Customer code
            </button>
            <button
              type="button"
              className="salon-start-choice__btn salon-start-choice__btn--ghost"
              onClick={goFaceIdentity}
            >
              Capture face
            </button>
            {showMobile ? (
              <button
                type="button"
                className="salon-start-choice__btn salon-start-choice__btn--ghost"
                onClick={goMobileLookup}
              >
                Enter mobile number
              </button>
            ) : null}
          </div>

          <button
            type="button"
            className="salon-start-choice__back"
            onClick={() => navigate('/nyafaz-ep/salon', { replace: true })}
          >
            Back
          </button>
        </main>
      </div>
    </div>
  );
}
